'use client';

import { type PropsWithChildren, memo, useRef } from 'react';
import { debounce } from 'instantsearch.js/es/lib/utils/debounce';
import { InstantSearchNext } from 'react-instantsearch-nextjs';
import ConfigureSearch from '@app/plp/components/ConfigureSearch/ConfigureSearch';
import ProductList from '@app/plp/components/ProductList/ProductList';
import { TrackingParams } from '@libs/analytics';
import { useEffectOnce } from '@libs/common-next';
import {
    filterOutSeoFriendlyFacets,
    mapFacetValuesToUrl,
} from '@app/plp/utils/facetedNavigationClientUtils';

import type {
    IAlgoliaRouteState,
    IUiState,
    SFCCFilterMap,
} from '@libs/algolia';
import {
    AlgoliaIndice,
    FILTERS,
    ORDERED_FILTERS,
    createAlgoliaClient,
    useFeaturedBrand,
    useSearchPage,
} from '@libs/algolia';

import type { UiState } from 'instantsearch.js';
import type { InstantSearchNextRouting } from 'react-instantsearch-nextjs';
import type { SeoMetadata } from '@app/plp/types/seo';
import type { Facets } from '@app/plp/types/facetedNavigation';

import {
    VirtualNumericMenu,
    VirtualPagination,
    VirtualRangeInput,
    VirtualRefinementList,
    VirtualSearchBox,
    VirtualSortBy,
    getQueryParamsFromState,
    getRefinementsFromQueryParams,
    getRouteFromState,
    getStateFromRoute,
} from './utils';

export type SearchLayoutProps = {
    entryId?: string;
    preFilters: string[];
    preFilterLogic: string;
    forceCategoryH1?: boolean;
    urlFilters: SFCCFilterMap[] | Facets[];
    algoliaCategoryPath: string;
    seoMetadata: SeoMetadata;
    isCategoryActive: boolean;
};

export type FacetHitProps = {
    value: string;
};

export type FacetProps = {
    facetHits: FacetHitProps[];
};

const ComponentRefinementList = () =>
    ORDERED_FILTERS.filter(f => !f.isNumeric).map(f => (
        <VirtualRefinementList
            key={`${f.attribute}-${f.urlName}`}
            attribute={f.attribute}
        />
    ));

function SearchLayout({
    entryId,
    preFilters,
    preFilterLogic,
    forceCategoryH1,
    urlFilters,
    algoliaCategoryPath,
    seoMetadata,
    isCategoryActive,
}: PropsWithChildren<SearchLayoutProps>) {
    const isSearchPage = useSearchPage();
    const isFeaturedBrand = useFeaturedBrand();
    const containerRef = useRef<HTMLDivElement>(null);
    const isFirstLoad = useRef(true);
    const onStateChange = (updatedSearchState: {
        uiState: UiState;
        setUiState: (
            uiState: UiState | ((previousUiState: UiState) => UiState)
        ) => void;
    }) => {
        if (isFirstLoad.current) {
            // PLP is SSRed with initial values for the InstantSearch instance then
            // when the component is hydrated on the client side its state tries to be updated with its current values,
            // which causes its onChange to fire again, thus launching a "duplicate" request to the server
            // this condition prevents the first trigger on client side when the app is still loading
            isFirstLoad.current = false;
            return;
        }
        updatedSearchState.setUiState(updatedSearchState.uiState);
    };

    const debouncedOnStageChange = debounce(onStateChange, 200);

    const routing: InstantSearchNextRouting<UiState, UiState> = {
        router: {
            // overriding default behavior so that the whole page does not re-render when adding/removing brands
            push(url: string) {
                if (window.history) {
                    /**
                     * Since Next version 14, window.history.pushState is monkey patched to work with Next Router
                     * Setting { _N: true } data, triggers the original pushState method, without triggering Next's monkey patched version
                     *
                     * This is a temporary solution
                     *
                     * @see https://github.com/vercel/next.js/blob/5969a097b44eae1dd314e8f84d13733edf73af3e/packages/next/src/client/components/app-router.tsx#L481
                     */
                    window.history.pushState({ _N: true }, '', url);
                }
            },
            createURL({ qsModule, routeState, location }) {
                const pathnameToUse = mapFacetValuesToUrl(routeState, location);

                const queryParameters = getQueryParamsFromState(
                    filterOutSeoFriendlyFacets(routeState),
                    isSearchPage
                );

                const queryString = qsModule.stringify(queryParameters, {
                    addQueryPrefix: true,
                    arrayFormat: 'repeat',
                });

                return `${location.protocol}//${location.host}${pathnameToUse}${queryString}`;
            },
            parseURL({ qsModule, location }) {
                const parsedqs = qsModule.parse(location.search.slice(1));
                return getRefinementsFromQueryParams(parsedqs, urlFilters);
            },
        },

        stateMapping: {
            stateToRoute(uiState: unknown) {
                const indexUiState = (uiState as IUiState)[
                    AlgoliaIndice.PRODUCTS
                ];
                return getRouteFromState(indexUiState);
            },
            routeToState(routeState: unknown) {
                return {
                    [AlgoliaIndice.PRODUCTS]: getStateFromRoute(
                        routeState as IAlgoliaRouteState,
                        isSearchPage
                    ),
                };
            },
        },
    };

    useEffectOnce(() => {
        window.sessionStorage.pdpFindingMethod =
            !isSearchPage || isFeaturedBrand
                ? TrackingParams.BROWSE_REFERER
                : TrackingParams.SEARCH_REFERER;

        const handleFacets = (data: Event) => {
            const client = createAlgoliaClient();
            const index = client.initIndex(AlgoliaIndice.PRODUCTS);
            const eventData = data as CustomEvent;
            const { facetName, facetKeys, variant } = eventData.detail;

            const indices = facetKeys.map((key: string) =>
                index.searchForFacetValues(facetName, key, {
                    maxFacetHits: 100,
                })
            );

            const getFacetResults = (facetData: FacetProps[]) => {
                const hits: FacetHitProps[] = [];

                facetData.forEach((facet: FacetProps) =>
                    facet.facetHits.forEach(
                        (facetHit: FacetHitProps) =>
                            facetKeys?.forEach((key: string) =>
                                facetHit.value === key
                                    ? hits.push(facetHit)
                                    : null
                            )
                    )
                );

                window.dispatchEvent(
                    new CustomEvent('algoliaDisjunctiveFacets', {
                        detail: {
                            variant,
                            hits,
                        },
                    })
                );
            };

            Promise.all(indices).then(facetData =>
                getFacetResults(facetData as FacetProps[])
            );
        };

        window.addEventListener('getAlgoliaDisjunctiveFacets', handleFacets);

        return () => {
            window.removeEventListener(
                'getAlgoliaDisjunctiveFacets',
                handleFacets
            );
        };
    });

    return (
        <div ref={containerRef} className="instant-search-container">
            <InstantSearchNext
                searchClient={createAlgoliaClient()}
                indexName={AlgoliaIndice.PRODUCTS}
                onStateChange={debouncedOnStageChange}
                routing={routing}
                insights
            >
                {/* Purpose of these virtual widgets is so that instant search knows
                    what components will be used (some are rendered client side) */}
                <ComponentRefinementList />
                <VirtualPagination />
                <VirtualNumericMenu
                    attribute={FILTERS.RATING.ATTRIBUTE}
                    items={FILTERS.RATING.ITEMS}
                />
                <VirtualSortBy isSearchPage />
                <VirtualRangeInput attribute={FILTERS.RANGE.ATTRIBUTE} />
                <VirtualSearchBox />
                <ConfigureSearch
                    preFilters={preFilters}
                    preFilterLogic={preFilterLogic}
                    algoliaCategoryPath={algoliaCategoryPath}
                />
                <ProductList
                    entryId={entryId}
                    forceCategoryH1={forceCategoryH1}
                    seoMetadata={seoMetadata}
                    isCategoryActive={isCategoryActive}
                />
            </InstantSearchNext>
        </div>
    );
}

export default memo(SearchLayout);
